import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { useTranslation } from '../../hooks/useTranslation';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';
import { profileAPI } from '../../services/api';
import { useToast } from '../ui/use-toast';
import { Sun, Moon, Monitor } from 'lucide-react';

const PreferencesTab = ({ profile, onUpdate }) => {
  const { t } = useTranslation();
  const { language, changeLanguage } = useLanguage();
  const { theme, setLightTheme, setDarkTheme } = useTheme();
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    language: profile?.preferences?.language || language,
    timezone: profile?.preferences?.timezone || 'UTC',
    email_notifications: profile?.preferences?.email_notifications ?? true,
    currency: profile?.preferences?.currency || 'EUR',
    theme: theme || 'light',
  });
  const [saving, setSaving] = useState(false);

  const handleThemeChange = (newTheme) => {
    setFormData({ ...formData, theme: newTheme });
    if (newTheme === 'dark') {
      setDarkTheme();
    } else {
      setLightTheme();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await profileAPI.updatePreferences(formData);
      changeLanguage(formData.language);
      toast({
        title: t('common.success'),
        description: 'Préférences mises à jour avec succès',
      });
      onUpdate();
    } catch (error) {
      toast({
        title: t('common.error'),
        description: error.response?.data?.detail || 'Échec de la mise à jour des préférences',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white">{t('profile.preferences.title')}</h3>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{t('profile.preferences.subtitle')}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 max-w-md">
        {/* Theme Selection */}
        <div>
          <Label className="mb-3 block">Thème de l'interface</Label>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => handleThemeChange('light')}
              className={`flex items-center justify-center space-x-2 p-4 rounded-lg border-2 transition-all ${
                formData.theme === 'light'
                  ? 'border-emerald-600 bg-emerald-50 dark:bg-emerald-900/30'
                  : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
              }`}
            >
              <Sun className={`h-5 w-5 ${formData.theme === 'light' ? 'text-emerald-600' : 'text-slate-500'}`} />
              <span className={`font-medium ${formData.theme === 'light' ? 'text-emerald-600' : 'text-slate-700 dark:text-slate-300'}`}>
                Clair
              </span>
            </button>
            <button
              type="button"
              onClick={() => handleThemeChange('dark')}
              className={`flex items-center justify-center space-x-2 p-4 rounded-lg border-2 transition-all ${
                formData.theme === 'dark'
                  ? 'border-emerald-600 bg-emerald-50 dark:bg-emerald-900/30'
                  : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
              }`}
            >
              <Moon className={`h-5 w-5 ${formData.theme === 'dark' ? 'text-emerald-600' : 'text-slate-500'}`} />
              <span className={`font-medium ${formData.theme === 'dark' ? 'text-emerald-600' : 'text-slate-700 dark:text-slate-300'}`}>
                Sombre
              </span>
            </button>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
            Le mode sombre réduit la fatigue oculaire dans les environnements peu éclairés.
          </p>
        </div>

        <div>
          <Label htmlFor="language">{t('profile.preferences.language')}</Label>
          <Select
            value={formData.language}
            onValueChange={(value) => setFormData({ ...formData, language: value })}
          >
            <SelectTrigger className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="en">English</SelectItem>
              <SelectItem value="fr">Français</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="timezone">{t('profile.preferences.timezone')}</Label>
          <Select
            value={formData.timezone}
            onValueChange={(value) => setFormData({ ...formData, timezone: value })}
          >
            <SelectTrigger className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="UTC">UTC (GMT+0)</SelectItem>
              <SelectItem value="Europe/Paris">Paris (GMT+1)</SelectItem>
              <SelectItem value="Europe/London">London (GMT+0)</SelectItem>
              <SelectItem value="America/New_York">New York (GMT-5)</SelectItem>
              <SelectItem value="America/Los_Angeles">Los Angeles (GMT-8)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="currency">Devise par défaut</Label>
          <Select
            value={formData.currency}
            onValueChange={(value) => setFormData({ ...formData, currency: value })}
          >
            <SelectTrigger className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="EUR">Euro (€)</SelectItem>
              <SelectItem value="USD">Dollar US ($)</SelectItem>
              <SelectItem value="GBP">Livre Sterling (£)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center justify-between py-4 border rounded-lg px-4 dark:border-slate-700">
          <div className="space-y-0.5">
            <Label>{t('profile.preferences.notifications')}</Label>
            <p className="text-sm text-slate-500 dark:text-slate-400">{t('profile.preferences.notificationsDesc')}</p>
          </div>
          <Switch
            checked={formData.email_notifications}
            onCheckedChange={(checked) => setFormData({ ...formData, email_notifications: checked })}
          />
        </div>

        <div className="flex justify-end pt-4">
          <Button type="submit" disabled={saving} className="bg-gradient-to-r from-emerald-600 to-emerald-600 hover:from-emerald-700 hover:to-emerald-700 text-white">
            {saving ? t('common.loading') : t('common.save')}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default PreferencesTab;
